import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MapPin, Navigation, Check, AlertCircle, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { clearApiCache } from '@/lib/sw-register';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LocationManager = ({ refreshData }) => {
  const [method, setMethod] = useState('gps'); // 'gps', 'address', 'manual'
  const [loading, setLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [formData, setFormData] = useState({
    address: '',
    latitude: '',
    longitude: '',
    otp: ''
  });
  const [confirmed, setConfirmed] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const markerRef = useRef(null);
  const token = localStorage.getItem('mojo_token');

  // OTP Timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpTimer]);

  // Fetch current location from backend
  useEffect(() => {
    fetchCurrentLocation();
  }, []);

  // Initialize map
  useEffect(() => {
    if (mapRef.current && window.google && !googleMapRef.current) {
      const initialCenter = currentLocation 
        ? { lat: currentLocation.latitude, lng: currentLocation.longitude }
        : { lat: -26.2041, lng: 28.0473 }; // Johannesburg

      googleMapRef.current = new window.google.maps.Map(mapRef.current, {
        center: initialCenter,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false
      });

      // Add marker if location exists
      if (currentLocation) {
        addMarker(currentLocation.latitude, currentLocation.longitude);
      }

      // Allow clicking on map to set location
      googleMapRef.current.addListener('click', (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        setFormData(prev => ({
          ...prev,
          latitude: lat.toFixed(6),
          longitude: lng.toFixed(6)
        }));
        addMarker(lat, lng);
        reverseGeocode(lat, lng);
      });
    }
  }, [mapRef.current, currentLocation]);

  const fetchCurrentLocation = async () => {
    try {
      const response = await axios.get(`${API}/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.location) {
        setCurrentLocation(response.data.location);
        setFormData({
          address: response.data.location.address || '',
          latitude: response.data.location.latitude || '',
          longitude: response.data.location.longitude || ''
        });
        setConfirmed(true);
      }
    } catch (error) {
      console.error('Failed to fetch location');
    }
  };

  const addMarker = (lat, lng) => {
    if (!googleMapRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Add new marker
    markerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: googleMapRef.current,
      draggable: true,
      animation: window.google.maps.Animation.DROP,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: '#F2B10C',
        fillOpacity: 1,
        strokeColor: '#1B0C34',
        strokeWeight: 3
      }
    });

    // Update coordinates when marker is dragged
    markerRef.current.addListener('dragend', (e) => {
      const newLat = e.latLng.lat();
      const newLng = e.latLng.lng();
      setFormData(prev => ({
        ...prev,
        latitude: newLat.toFixed(6),
        longitude: newLng.toFixed(6)
      }));
      reverseGeocode(newLat, newLng);
    });

    // Center map on marker
    googleMapRef.current.setCenter({ lat, lng });
  };

  // One-shot flag — the Google Maps Geocoder is locked down on this
  // project's API key (REQUEST_DENIED). We surface a hint the first
  // time it fails so the user knows to type the address manually,
  // instead of staring at a silent empty field.
  const geocoderHintShownRef = useRef(false);

  const reverseGeocode = async (lat, lng) => {
    if (!window.google) return;

    const geocoder = new window.google.maps.Geocoder();
    try {
      const result = await geocoder.geocode({ location: { lat, lng } });
      if (result.results[0]) {
        setFormData(prev => ({
          ...prev,
          address: result.results[0].formatted_address
        }));
      }
    } catch (error) {
      // REQUEST_DENIED means the Maps key doesn't have Geocoding API
      // enabled / authorised for this referrer. User can still proceed
      // by typing the address manually in the Address or Manual tab.
      const denied = String(error?.code || error?.message || '').includes('REQUEST_DENIED');
      if (denied && !geocoderHintShownRef.current) {
        geocoderHintShownRef.current = true;
        toast.message('Auto-address is unavailable — please type your address in the Address tab.');
      }
      // eslint-disable-next-line no-console
      console.warn('Reverse geocoding failed (proceeding without auto-address):', error);
    }
  };

  const handleUseGPS = () => {
    setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          setFormData(prev => ({
            ...prev,
            latitude: lat.toFixed(6),
            longitude: lng.toFixed(6)
          }));
          
          addMarker(lat, lng);
          reverseGeocode(lat, lng);
          toast.success('Location captured successfully!');
          setLoading(false);
        },
        (error) => {
          toast.error('Unable to get your location. Please check permissions.');
          setLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      toast.error('Geolocation is not supported by your browser');
      setLoading(false);
    }
  };

  const handleAddressSelect = (address) => {
    setFormData(prev => ({ ...prev, address }));
    // Coords come from the Places autocomplete's onLocationSelect
    // callback below — we don't call the Geocoder explicitly, because
    // it tends to be blocked when Places isn't, and AddressAutocomplete
    // already gives us geometry alongside formatted_address.
  };

  const handlePlaceLocation = ({ address, latitude, longitude }) => {
    if (latitude == null || longitude == null) return;
    setFormData(prev => ({
      ...prev,
      address,
      latitude:  Number(latitude).toFixed(6),
      longitude: Number(longitude).toFixed(6),
    }));
    addMarker(Number(latitude), Number(longitude));
  };

  const handleManualCoordinates = () => {
    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);

    if (isNaN(lat) || isNaN(lng)) {
      toast.error('Please enter valid coordinates');
      return;
    }

    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      toast.error('Coordinates out of range');
      return;
    }

    addMarker(lat, lng);
    reverseGeocode(lat, lng);
    toast.success('Location set from coordinates');
  };

  const handleRequestOTP = async () => {
    if (!formData.latitude || !formData.longitude || !formData.address) {
      toast.error('Please set your location first');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/merchant/request-location-otp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setOtpSent(true);
      setOtpTimer(300); // 5 minutes
      setFormData(prev => ({ ...prev, otp: '' }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmLocation = async () => {
    if (!formData.latitude || !formData.longitude) {
      toast.error('Please set your location first');
      return;
    }

    if (!formData.address) {
      toast.error('Please provide an address');
      return;
    }

    if (!otpSent) {
      toast.error('Please request OTP first');
      return;
    }

    if (!formData.otp || formData.otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/merchant/update-location`,
        {
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude),
          address: formData.address,
          otp: formData.otp
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // The Phase 4 service worker keeps /wallet/balance in API_CACHE
      // (stale-while-revalidate). Without an explicit cache nuke here,
      // the next read — whether the parent refreshData, a navigation
      // back to this page, or a hard reload — serves the PRE-update
      // location dict while the SW fetches fresh in the background.
      // That gave Frank the "OTP verified but address didn't update"
      // bug. Clear first, then refresh, then re-pull our own snapshot.
      await clearApiCache();
      toast.success('Location confirmed successfully!');
      setConfirmed(true);
      setOtpSent(false);
      setOtpTimer(0);
      setCurrentLocation({
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        address: formData.address
      });
      if (refreshData) refreshData();
      // Re-read /wallet/balance ourselves so the component's local
      // `currentLocation` reflects the persisted value (defensive — the
      // parent's refreshData already does this, but if this component is
      // ever mounted without a parent refresh prop we still want it).
      fetchCurrentLocation();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update location');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-[#1B0C34] flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Business Location
            </CardTitle>
            <CardDescription>
              Set your location so customers can find you
            </CardDescription>
          </div>
          {confirmed && (
            <Badge className="bg-green-600">
              <Check className="w-3 h-3 mr-1" />
              Confirmed
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Alert */}
        {!confirmed && (
          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-orange-800">
              <strong>Action Required:</strong> Please confirm your business location so customers can find you using the Merchant Finder.
            </div>
          </div>
        )}

        {/* Method Selection */}
        <div className="flex gap-2">
          <Button
            variant={method === 'gps' ? 'default' : 'outline'}
            className={method === 'gps' ? 'bg-[#145539] hover:bg-[#1e7049]' : ''}
            onClick={() => setMethod('gps')}
            size="sm"
          >
            <Navigation className="w-4 h-4 mr-1" />
            Use GPS
          </Button>
          <Button
            variant={method === 'address' ? 'default' : 'outline'}
            className={method === 'address' ? 'bg-[#145539] hover:bg-[#1e7049]' : ''}
            onClick={() => setMethod('address')}
            size="sm"
          >
            <MapPin className="w-4 h-4 mr-1" />
            Address
          </Button>
          <Button
            variant={method === 'manual' ? 'default' : 'outline'}
            className={method === 'manual' ? 'bg-[#145539] hover:bg-[#1e7049]' : ''}
            onClick={() => setMethod('manual')}
            size="sm"
          >
            Manual
          </Button>
        </div>

        {/* GPS Method */}
        {method === 'gps' && (
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              Click the button to use your device's GPS to capture your current location.
            </p>
            <Button
              onClick={handleUseGPS}
              disabled={loading}
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Getting Location...
                </>
              ) : (
                <>
                  <Navigation className="w-4 h-4 mr-2" />
                  Capture GPS Location
                </>
              )}
            </Button>
          </div>
        )}

        {/* Address Method */}
        {method === 'address' && (
          <div className="space-y-2">
            <Label>Business Address</Label>
            <AddressAutocomplete
              placeholder="Start typing your business address..."
              value={formData.address}
              onChange={(e) => handleAddressSelect(e.target.value)}
              onLocationSelect={handlePlaceLocation}
              name="business_address"
            />
            <p className="text-xs text-gray-500">
              Select your address from the dropdown to auto-fill coordinates
            </p>
          </div>
        )}

        {/* Manual Method */}
        {method === 'manual' && (
          <div className="space-y-3">
            <div>
              <Label>Address</Label>
              <Input
                placeholder="Your business address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Latitude</Label>
                <Input
                  type="number"
                  step="0.000001"
                  placeholder="-26.2041"
                  value={formData.latitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                />
              </div>
              <div>
                <Label>Longitude</Label>
                <Input
                  type="number"
                  step="0.000001"
                  placeholder="28.0473"
                  value={formData.longitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                />
              </div>
            </div>
            <Button
              onClick={handleManualCoordinates}
              variant="outline"
              className="w-full"
              size="sm"
            >
              Set Location from Coordinates
            </Button>
            <p className="text-xs text-gray-500">
              You can also click on the map to set your location
            </p>
          </div>
        )}

        {/* Map */}
        <div className="space-y-2">
          <Label>Location Preview</Label>
          <div ref={mapRef} className="w-full h-64 rounded-lg border border-gray-300" />
          <p className="text-xs text-gray-500">
            Click or drag the marker on the map to adjust your exact location
          </p>
        </div>

        {/* Current Coordinates Display */}
        {(formData.latitude && formData.longitude) && (
          <div className="p-3 bg-gray-50 rounded-lg space-y-1">
            <p className="text-sm font-medium text-gray-700">Current Location:</p>
            <p className="text-xs text-gray-600 break-all">
              <strong>Address:</strong> {formData.address || 'Not set'}
            </p>
            <p className="text-xs text-gray-600">
              <strong>Coordinates:</strong> {formData.latitude}, {formData.longitude}
            </p>
          </div>
        )}

        {/* OTP Section */}
        <div className="border-t pt-4 space-y-3">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 font-medium mb-1">
              🔒 Security Verification Required
            </p>
            <p className="text-xs text-blue-700">
              For security, we'll send a One-Time Password (OTP) to your registered mobile number to confirm this location update.
            </p>
          </div>

          {!otpSent ? (
            <Button
              onClick={handleRequestOTP}
              disabled={loading || !formData.latitude || !formData.longitude || !formData.address}
              className="w-full bg-[#F2B10C] hover:bg-[#d99a0a] text-[#1B0C34] font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending OTP...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Request OTP
                </>
              )}
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  ✓ OTP sent to your mobile number
                </p>
                {otpTimer > 0 && (
                  <p className="text-xs text-green-700 mt-1">
                    Valid for: {Math.floor(otpTimer / 60)}:{(otpTimer % 60).toString().padStart(2, '0')}
                  </p>
                )}
              </div>

              <div>
                <Label>Enter 6-Digit OTP</Label>
                <Input
                  type="text"
                  maxLength="6"
                  placeholder="000000"
                  value={formData.otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setFormData(prev => ({ ...prev, otp: value }));
                  }}
                  className="text-center text-2xl tracking-widest font-bold"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleRequestOTP}
                  disabled={loading || otpTimer > 240}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  Resend OTP
                </Button>
                <Button
                  onClick={() => {
                    setOtpSent(false);
                    setOtpTimer(0);
                    setFormData(prev => ({ ...prev, otp: '' }));
                  }}
                  variant="outline"
                  size="sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Confirm Button */}
        <Button
          onClick={handleConfirmLocation}
          disabled={loading || !otpSent || !formData.otp || formData.otp.length !== 6}
          className="w-full bg-[#145539] hover:bg-[#1e7049] text-white font-semibold"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Verifying...
            </>
          ) : (
            <>
              <Check className="w-4 h-4 mr-2" />
              Verify OTP & Confirm Location
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default LocationManager;
